import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { getPartName } from '@app/utils/commonUtils';
import { CatHeading } from '@blocks-react/components/Heading';
import { CatButton } from '@blocks-react/components/Button';
import styles from '../KitPartsCards.module.scss';
import { normalizeUrl } from '@app/utils';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useBreakpoint } from '@app/hooks';
import { getKitPartsDetails } from '@app/store/products/actions';
import {
  BUTTON_VARIANTS,
  PAGE_BREAKPOINTS,
  STATUS
} from '@app/constants/commonConstants';
import Skeleton from '@app/components/common/Blocks/Skeleton/Skeleton';
import ErrorWarning from '@app/components/common/Blocks/ErrorWarning/ErrorWarning';
import Conditional from '@app/components/common/Conditional';
import { PCC_RELEASE_HEADLESS_PDP_FLAG } from '@app/constants/featureFlags';

const KitPartDrawerContainer = ({ part }) => {
  const dispatch = useDispatch();
  const [t] = useTranslation();
  const productDisplayUrl = normalizeUrl(part.productURL);
  const isMD = useBreakpoint(PAGE_BREAKPOINTS.MD);

  useEffect(() => {
    dispatch(getKitPartsDetails(part.partNumber));
  }, [dispatch, part]);

  const getKitPartsDetailsCallStatus =
    useSelector(state => state.products.getKitPartsDetailsStatus) ||
    STATUS.PENDING;
  const isGetKitPartsDetailsLoading =
    getKitPartsDetailsCallStatus === STATUS.PENDING;
  const isGetKitPartsDetailsRejected =
    getKitPartsDetailsCallStatus === STATUS.REJECTED;
  const manufacturer =
    useSelector(state => state.products.kitPartsDetails?.manufacturer) || '';
  const attributes =
    useSelector(state => state.products.kitPartsDetails?.attributes) || [];

  const esiteURL = useSelector(s => s.common?.esiteURL);
  const isPDPHeadlessFeatureFlagEnable =
    useSelector(s => s.featureFlag?.[PCC_RELEASE_HEADLESS_PDP_FLAG]) ?? false;

  const urlBasedonFeatureFlag = isPDPHeadlessFeatureFlagEnable
    ? `${esiteURL}/product/${part.partNumber}`
    : productDisplayUrl;

  const containerLoader = (
    <>
      <Skeleton heightInPx={112} className="mb-1" />
      <Skeleton heightInPx={456} />
      <div slot="footer">
        <Skeleton heightInPx={40} width={97} />
      </div>
    </>
  );

  return (
    <>
      <CatHeading variant="title-sm" slot="header">
        {getPartName(part.partNumber, part.name)}
      </CatHeading>

      <Conditional
        test={!isGetKitPartsDetailsLoading}
        fallback={containerLoader}
      >
        <Conditional
          test={!isGetKitPartsDetailsRejected}
          fallback={<ErrorWarning />}
        >
          <div
            className={`d-flex
                    ${styles['kit-parts-cards__drawer--container']}`}
          >
            <img
              loading="lazy"
              width="112px"
              height="112px"
              src={`${part.thumbnail}?wid=112&hei=112`}
              alt="parts"
            />
            <div
              className={`d-flex flex-column
                    ${styles['kit-parts-cards__drawer--container-details']}`}
            >
              <CatHeading variant={isMD ? 'body' : 'body-sm'}>
                {part.shortDescription}
              </CatHeading>
              <CatHeading variant="body-sm">{`${t(
                'CPG_BRAND'
              )}: ${manufacturer}`}</CatHeading>
              <a href={urlBasedonFeatureFlag} data-testid="view-part-link">
                <CatButton variant={BUTTON_VARIANTS.GHOST}>
                  {t('KITS_VIEW_PART')}
                </CatButton>
              </a>
            </div>
          </div>
          <Conditional
            test={attributes.length > 0}
            fallback={
              <CatHeading variant="body">
                {t('KITS_NO_SPECS_AVAILABLE')}
              </CatHeading>
            }
          >
            {attributes.map(attribute => (
              <CatHeading variant="body" key={attribute.name}>
                <b>{`${attribute.name}: `}</b>
                {attribute.value}
              </CatHeading>
            ))}
          </Conditional>

          <div slot="footer">
            <a href={urlBasedonFeatureFlag} data-testid="view-part-footer-link">
              <CatButton variant={BUTTON_VARIANTS.GHOST}>
                {t('KITS_VIEW_PART')}
              </CatButton>
            </a>
          </div>
        </Conditional>
      </Conditional>
    </>
  );
};

KitPartDrawerContainer.propTypes = {
  part: PropTypes.shape({
    name: PropTypes.string,
    partNumber: PropTypes.string,
    productURL: PropTypes.string,
    quantity: PropTypes.number,
    shortDescription: PropTypes.string,
    longDescription: PropTypes.string,
    thumbnail: PropTypes.string
  })
};

export default KitPartDrawerContainer;
